import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Feather, Users, Building2, PackageCheck, ShieldCheck, Award,
  Truck, Sparkles, Globe, ArrowRight, Instagram, Music2, Play, Send,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MediaGallery from "../components/MediaGallery";
import PreuvesEnvoi from "../components/PreuvesEnvoi";
import { LEGAL, BRAND } from "../lib/brand";

// -------- Page metadata (SEO) --------
if (typeof document !== "undefined") {
  document.title = "La Maison — MAISON DOUBA · Notre histoire, notre équipe & nos engagements";
}

// -------- Reusable section header --------
const SectionHeader = ({ eyebrow, title, italic, description }) => (
  <div className="max-w-3xl">
    <p className="label-caps text-[color:var(--gold)] mb-4">{eyebrow}</p>
    <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-white">
      {title} {italic && <em className="italic text-[color:var(--gold-soft)]">{italic}</em>}
    </h2>
    {description && (
      <p className="text-white/60 font-light text-base md:text-lg mt-6 leading-relaxed">{description}</p>
    )}
  </div>
);

// -------- Stats pill --------
const StatCard = ({ number, label, sub }) => (
  <div className="border border-white/10 p-8 hover:border-[color:var(--gold)]/30 transition-colors">
    <p className="font-serif text-4xl md:text-5xl text-[color:var(--gold)]">{number}</p>
    <p className="label-caps text-white/80 mt-3">{label}</p>
    {sub && <p className="text-white/40 text-xs mt-2">{sub}</p>}
  </div>
);

const AboutPage = () => {
  const location = useLocation();

  // Smooth-scroll to anchor when arriving with a hash (e.g. /a-propos#envois)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // Wait a tick so the section is mounted
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <div className="bg-[#050505] text-white">
      <Navbar />

      {/* ================================================================
          HERO
      ================================================================= */}
      <section className="relative pt-40 md:pt-52 pb-24 md:pb-32 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.15) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <p className="label-caps text-[color:var(--gold)] mb-6">La Maison — Depuis Paris</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-white">
            L'art d'offrir<br />
            <em className="italic text-[color:var(--gold-soft)]">l'invisible.</em>
          </h1>
          <p className="text-white/60 font-light text-lg md:text-xl mt-10 max-w-2xl leading-relaxed">
            MAISON DOUBA sélectionne pour vous les plus belles fragrances et montres du monde arabe,
            européen et oriental. Nos flacons proviennent de fournisseurs officiels, sont conservés dans
            un stock climatisé et préparés à la main, pièce par pièce, pour vous parvenir intacts.
          </p>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard number="+22" label="Fragrances curatées" sub="100 ml · flacons scellés" />
            <StatCard number="100%" label="Origine authentique" sub="fournisseurs officiels" />
            <StatCard number="48–72h" label="Expédition" sub="Colissimo · Chronopost" />
            <StatCard number="3" label="Pays TikTok Shop" sub="France · Italie · Espagne" />
          </div>
        </div>
      </section>

      {/* ================================================================
          1 · NOTRE HISTOIRE
      ================================================================= */}
      <section id="histoire" className="border-t border-white/5 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <div className="md:col-span-5">
            <Feather size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
            <SectionHeader
              eyebrow="Chapitre I"
              title="Notre"
              italic="histoire."
            />
          </div>
          <div className="md:col-span-7 space-y-6 text-white/70 font-light leading-relaxed">
            <p className="text-lg">
              Tout commence par une conviction : le luxe n'est pas dans le prix, il est dans l'authenticité.
              Chaque parfum a une âme, chaque montre raconte une histoire — encore faut-il pouvoir les découvrir
              sans doute, sans intermédiaire, sans copie.
            </p>
            <p>
              MAISON DOUBA — nom porté par sa fondatrice, {LEGAL.owner} — est née de ce refus du compromis.
              Nous parcourons les plus grandes maisons du Golfe et d'Europe pour rapporter en France des flacons
              authentiques, dans leur emballage d'origine, avec leurs codes sériels intacts.
            </p>
            <p>
              Depuis {LEGAL.address.split(",").pop().trim() || "Paris"}, notre atelier prépare chaque commande
              à la main. Une carte manuscrite, une pochette d'écrin, un mot de la maison. Parce que recevoir
              un parfum ne devrait jamais ressembler à ouvrir un colis Amazon.
            </p>
            <div className="pt-4 flex items-center gap-3">
              <div className="h-px w-12 bg-[color:var(--gold)]" />
              <p className="label-caps text-white/50 text-[10px]">{LEGAL.company_name} — SIRET {LEGAL.siret}</p>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-12 mt-16">
          <MediaGallery category="histoire" columns={3} />
        </div>
      </section>

      {/* ================================================================
          2 · NOTRE ÉQUIPE
      ================================================================= */}
      <section id="equipe" className="border-t border-white/5 py-24 md:py-32 bg-[#0a0a0a]/40">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <Users size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
            <SectionHeader
              eyebrow="Chapitre II"
              title="Les visages de"
              italic="la Maison."
              description="Une équipe restreinte, passionnée. Nous connaissons chaque flacon par cœur, chaque note olfactive, chaque client. C'est le prix — et le luxe — d'une maison à taille humaine."
            />
          </div>
          <MediaGallery category="equipe" columns={3} />
        </div>
      </section>

      {/* ================================================================
          3 · NOS FOURNISSEURS
      ================================================================= */}
      <section id="fournisseurs" className="border-t border-white/5 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start mb-16">
            <div className="md:col-span-5">
              <Building2 size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
              <SectionHeader
                eyebrow="Chapitre III"
                title="Des fournisseurs"
                italic="professionnels."
              />
            </div>
            <div className="md:col-span-7 space-y-5 text-white/70 font-light">
              <p className="text-lg">
                Nous ne nous approvisionnons qu'auprès de distributeurs officiels et de grossistes agréés
                des maisons que nous représentons — Lattafa, Armaf, Rasasi, Ard Al Zaafaran, Amiri, entre autres.
              </p>
              <ul className="space-y-3 pt-4 text-sm">
                {[
                  "Distributeurs européens agréés avec justificatifs douaniers",
                  "Lots contrôlés : numéros de série vérifiés à réception",
                  "Traçabilité complète du flacon jusqu'à votre porte",
                  "Facturation officielle disponible sur demande",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] mt-2 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <MediaGallery category="fournisseurs" columns={3} />
        </div>
      </section>

      {/* ================================================================
          4 · NOTRE STOCK
      ================================================================= */}
      <section id="stock" className="border-t border-white/5 py-24 md:py-32 bg-[#0a0a0a]/40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 20% 40%, rgba(212,175,55,0.15) 0%, transparent 60%)" }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <Sparkles size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
            <SectionHeader
              eyebrow="Chapitre IV"
              title="Un inventaire réel,"
              italic="prêt à être expédié."
              description="Contrairement aux boutiques dropshipping qui commandent après chaque vente, MAISON DOUBA détient physiquement chaque flacon présenté sur ce site. Un vrai atelier, un vrai stock, une vraie disponibilité — et une expédition qui part le jour même de votre commande."
            />
          </div>

          {/* Inventory stat band */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-14">
            <div className="border border-[color:var(--gold)]/30 p-6">
              <p className="font-serif text-4xl text-[color:var(--gold)]">+3000</p>
              <p className="label-caps text-white/80 text-[10px] mt-2">Flacons en stock</p>
              <p className="text-white/40 text-[11px] mt-1">Prêts à expédier</p>
            </div>
            <div className="border border-[color:var(--gold)]/30 p-6">
              <p className="font-serif text-4xl text-[color:var(--gold)]">22</p>
              <p className="label-caps text-white/80 text-[10px] mt-2">Fragrances curatées</p>
              <p className="text-white/40 text-[11px] mt-1">100 ml uniquement</p>
            </div>
            <div className="border border-[color:var(--gold)]/30 p-6">
              <p className="font-serif text-4xl text-[color:var(--gold)]">18–22°C</p>
              <p className="label-caps text-white/80 text-[10px] mt-2">Température</p>
              <p className="text-white/40 text-[11px] mt-1">Stock climatisé</p>
            </div>
            <div className="border border-[color:var(--gold)]/30 p-6">
              <p className="font-serif text-4xl text-[color:var(--gold)]">0%</p>
              <p className="label-caps text-white/80 text-[10px] mt-2">Dropshipping</p>
              <p className="text-white/40 text-[11px] mt-1">Zéro flacon commandé après-vente</p>
            </div>
          </div>

          {/* Quality operating rules */}
          <div className="grid md:grid-cols-3 gap-4 mb-14">
            <div className="border border-white/10 p-8 hover:border-[color:var(--gold)]/30 transition-colors">
              <p className="label-caps text-[color:var(--gold)] text-[10px] mb-3">Conservation</p>
              <p className="font-serif text-2xl text-white mb-3">Stockage climatisé</p>
              <p className="text-white/60 text-sm leading-relaxed">Température maîtrisée entre 18 et 22 °C, hygrométrie contrôlée. Aucun flacon exposé à la lumière directe — les huiles essentielles restent stables.</p>
            </div>
            <div className="border border-white/10 p-8 hover:border-[color:var(--gold)]/30 transition-colors">
              <p className="label-caps text-[color:var(--gold)] text-[10px] mb-3">Traçabilité</p>
              <p className="font-serif text-2xl text-white mb-3">Rotation stricte</p>
              <p className="text-white/60 text-sm leading-relaxed">Contrôle mensuel des dates de fabrication (batch codes vérifiés). Aucun stock antérieur à 24 mois n'est proposé à la vente.</p>
            </div>
            <div className="border border-white/10 p-8 hover:border-[color:var(--gold)]/30 transition-colors">
              <p className="label-caps text-[color:var(--gold)] text-[10px] mb-3">Sortie de stock</p>
              <p className="font-serif text-2xl text-white mb-3">Contrôle qualité</p>
              <p className="text-white/60 text-sm leading-relaxed">Chaque flacon inspecté avant préparation : intégrité du cellophane, code-barre, numéro de lot, spray fonctionnel.</p>
            </div>
          </div>

          {/* Media gallery — videos + photos of stock */}
          <div className="mb-4">
            <p className="label-caps text-[color:var(--gold)] mb-6">Galerie · Vidéos & photos du stock</p>
          </div>
          <MediaGallery category="stock" columns={3} />
        </div>
      </section>

      {/* ================================================================
          5 · PRÉPARATION DES COMMANDES
      ================================================================= */}
      <section id="preparation" className="border-t border-white/5 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start mb-16">
            <div className="md:col-span-5">
              <PackageCheck size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
              <SectionHeader
                eyebrow="Chapitre V"
                title="La préparation,"
                italic="un rituel."
              />
            </div>
            <div className="md:col-span-7 space-y-6 text-white/70 font-light">
              <ol className="space-y-6">
                {[
                  { n: "01", t: "Inspection", d: "Le flacon est sorti du stock, contrôlé sous lumière blanche : cellophane, capuchon, numéro de lot." },
                  { n: "02", t: "Écrin", d: "Placement dans un écrin en carton noir mat, protection intérieure en mousse haute densité." },
                  { n: "03", t: "Signature", d: "Ajout d'une carte manuscrite avec le prénom du destinataire — geste offert à chaque commande." },
                  { n: "04", t: "Expédition", d: "Colis suivi, tracking envoyé par email dès la remise au transporteur." },
                ].map((s) => (
                  <li key={s.n} className="flex gap-6 border-b border-white/5 pb-6 last:border-0">
                    <span className="label-caps text-[color:var(--gold)] text-xs shrink-0 pt-1">{s.n}</span>
                    <div>
                      <p className="font-serif text-xl text-white mb-1">{s.t}</p>
                      <p className="text-sm">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <MediaGallery category="preparation" columns={3} />
        </div>
      </section>

      {/* ================================================================
          6 · PREUVES D'ENVOI — Colis expédiés aux clients TikTok Shop
      ================================================================= */}
      <section id="envois" className="border-t border-white/5 py-24 md:py-32 bg-[#0a0a0a]/60 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(212,175,55,0.12) 0%, transparent 60%)" }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start mb-14">
            <div className="md:col-span-5">
              <Send size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
              <SectionHeader
                eyebrow="Chapitre VI"
                title="Preuves"
                italic="d'envoi."
              />
            </div>
            <div className="md:col-span-7 space-y-5 text-white/70 font-light">
              <p className="text-lg">
                Voici les colis réellement préparés et expédiés à nos clients — notamment via nos boutiques
                <strong className="text-white"> TikTok Shop France 🇫🇷, Italie 🇮🇹 et Espagne 🇪🇸</strong>.
              </p>
              <p>
                Contrairement au dropshipping, MAISON DOUBA emballe et expédie chaque commande depuis son
                propre atelier. Les étiquettes Colissimo, Colis Privé et Mondial Relay que vous voyez sont
                les nôtres — les informations personnelles de nos clients ont été masquées conformément au RGPD.
              </p>
              <div className="grid grid-cols-3 gap-3 pt-4">
                <div className="border border-[color:var(--gold)]/30 p-4 text-center">
                  <p className="font-serif text-2xl text-[color:var(--gold)]">+1000</p>
                  <p className="label-caps text-white/60 text-[9px] mt-1">Colis / mois</p>
                </div>
                <div className="border border-[color:var(--gold)]/30 p-4 text-center">
                  <p className="font-serif text-2xl text-[color:var(--gold)]">48–72h</p>
                  <p className="label-caps text-white/60 text-[9px] mt-1">Délai d'expédition</p>
                </div>
                <div className="border border-[color:var(--gold)]/30 p-4 text-center">
                  <p className="font-serif text-2xl text-[color:var(--gold)]">4</p>
                  <p className="label-caps text-white/60 text-[9px] mt-1">Transporteurs</p>
                </div>
              </div>
            </div>
          </div>
          <PreuvesEnvoi columns={4} />
          <p className="text-white/40 text-xs italic text-center mt-8 max-w-3xl mx-auto">
            🔒 Les noms, adresses et numéros de téléphone visibles sur les étiquettes ont été masqués pour respecter
            la vie privée de nos clients (RGPD — art. 5 & 6).
          </p>
        </div>
      </section>

      {/* ================================================================
          7 · CERTIFICATS & CONFORMITÉ
      ================================================================= */}
      <section id="certificats" className="border-t border-white/5 py-24 md:py-32 bg-[#0a0a0a]/40">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <ShieldCheck size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
            <SectionHeader
              eyebrow="Chapitre VII"
              title="Certificats"
              italic="& conformité."
              description="Notre engagement de transparence prend forme dans les documents officiels : certificats d'authenticité, tests laboratoire, conformité douanière et déclarations réglementaires."
            />
          </div>
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {[
              { label: "SIRET", value: LEGAL.siret },
              { label: "SIREN", value: LEGAL.siren },
              { label: "Code APE", value: `${LEGAL.ape_code} — ${LEGAL.ape_label}` },
              { label: "Forme légale", value: LEGAL.legal_form },
            ].map((c) => (
              <div key={c.label} className="border border-[color:var(--gold)]/20 p-6">
                <p className="label-caps text-[color:var(--gold)] text-[10px] mb-2">{c.label}</p>
                <p className="font-mono text-sm text-white break-words">{c.value}</p>
              </div>
            ))}
          </div>
          <MediaGallery category="certificats" columns={3} />
        </div>
      </section>

      {/* ================================================================
          7 · ENGAGEMENTS QUALITÉ
      ================================================================= */}
      <section id="qualite" className="border-t border-white/5 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <Award size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
            <SectionHeader
              eyebrow="Chapitre VIII"
              title="Nos engagements"
              italic="qualité."
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck, t: "Authenticité garantie", d: "Chaque flacon provient d'un distributeur officiel. Remboursement immédiat en cas de doute avéré." },
              { icon: Truck, t: "Livraison suivie", d: "Expédition sous 48 à 72h ouvrées. Colis assuré et suivi en temps réel jusqu'à votre porte." },
              { icon: PackageCheck, t: "Emballage soigné", d: "Écrin cartonné noir mat, mousse haute densité. Zéro risque de casse — 0,3% seulement de retours en 12 mois." },
              { icon: Sparkles, t: "Service concierge", d: "WhatsApp, email, téléphone. Réponse sous 4h ouvrées maximum. Un conseiller vous suit du premier message à la réception." },
            ].map((e) => (
              <div key={e.t} className="border border-white/10 p-8 hover:border-[color:var(--gold)]/30 transition-colors">
                <e.icon size={22} className="text-[color:var(--gold)] mb-4" strokeWidth={1.2} />
                <p className="font-serif text-2xl text-white mb-3">{e.t}</p>
                <p className="text-white/60 text-sm font-light leading-relaxed">{e.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <MediaGallery category="qualite" columns={3} />
          </div>
        </div>
      </section>

      {/* ================================================================
          8 · TIKTOK SHOP
      ================================================================= */}
      <section id="tiktok" className="border-t border-white/5 py-24 md:py-32 bg-gradient-to-b from-[#0a0a0a]/40 to-[#050505]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-6">
              <Globe size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
              <p className="label-caps text-[color:var(--gold)] mb-4">Chapitre IX</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-white mb-6">
                Aussi sur <em className="italic text-[color:var(--gold-soft)]">TikTok Shop.</em>
              </h2>
              <p className="text-white/60 font-light leading-relaxed mb-8">
                Retrouvez MAISON DOUBA sur TikTok Shop dans trois pays européens. Notre catalogue s'ouvre
                progressivement à de nouveaux marchés — chaque lancement s'accompagne d'une sélection
                spécialement adaptée aux goûts locaux.
              </p>
              <div className="space-y-3 mb-10">
                {[
                  { flag: "🇫🇷", country: "France", status: "Actif" },
                  { flag: "🇮🇹", country: "Italie", status: "Actif" },
                  { flag: "🇪🇸", country: "Espagne", status: "Actif" },
                ].map((c) => (
                  <div key={c.country} className="flex items-center justify-between border-b border-white/10 py-3">
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{c.flag}</span>
                      <span className="font-serif text-lg text-white">{c.country}</span>
                    </div>
                    <span className="label-caps text-[color:var(--gold)] text-[10px] border border-[color:var(--gold)]/40 px-3 py-1">
                      {c.status}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-3 opacity-50">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">🌍</span>
                    <span className="font-serif text-lg text-white/70 italic">D'autres pays bientôt</span>
                  </div>
                  <span className="label-caps text-white/40 text-[10px] border border-white/20 px-3 py-1">À venir</span>
                </div>
              </div>
              <a
                href={BRAND.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="about-tiktok-link"
                className="btn-outline inline-flex items-center gap-3"
              >
                <Music2 size={14} />
                Suivre @wattisera sur TikTok
                <ArrowRight size={14} />
              </a>
            </div>
            <div className="md:col-span-6">
              <MediaGallery category="tiktok" columns={2} />
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          9 · GALERIE PRINCIPALE
      ================================================================= */}
      <section id="galerie" className="border-t border-white/5 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <Play size={28} className="text-[color:var(--gold)] mb-6" strokeWidth={1.2} />
            <SectionHeader
              eyebrow="La Galerie"
              title="Photos & vidéos"
              italic="de la Maison."
              description="Une sélection en évolution constante. Nouvelles arrivages, essais parfumés, unboxings, moments d'atelier — regardez la maison vivre."
            />
          </div>
          <MediaGallery category="galerie" columns={4} variant="masonry" />
        </div>
      </section>

      {/* ================================================================
          CTA FINAL
      ================================================================= */}
      <section className="border-t border-white/5 py-24 md:py-32 text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="label-caps text-[color:var(--gold)] mb-6">La Maison vous attend</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8">
            Vous connaissez maintenant<br/><em className="italic text-[color:var(--gold-soft)]">notre visage.</em>
          </h2>
          <p className="text-white/60 font-light text-lg mb-12">
            Découvrez la sélection qui a demandé plus d'un an de recherches et de contrôles.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/#collection" data-testid="about-cta-collection" className="btn-gold inline-flex items-center gap-3">
              Découvrir la collection <ArrowRight size={14} />
            </Link>
            <Link to="/contact" data-testid="about-cta-contact" className="btn-outline inline-flex items-center gap-3">
              Nous écrire
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
